import styled from 'styled-components';
import InputMask from 'react-input-mask';
import { colors } from '../../../theme/colors';
import { typography } from '../../../theme/typography';

const getFontFamily = (props) => (props.theme && props.theme.typography && props.theme.typography.fontFamily && props.theme.typography.fontFamily.base) || 'sans-serif';
const getFontSizeSm = (props) => (props.theme && props.theme.typography && props.theme.typography.fontSize && props.theme.typography.fontSize.sm) || '14px';
const getFontSizeXs = (props) => (props.theme && props.theme.typography && props.theme.typography.fontSize && props.theme.typography.fontSize.xs) || '12px';
const getPrimaryColor = (props) => (props.theme && props.theme.colors && props.theme.colors.brand && props.theme.colors.brand.primary) || colors.brandDarkBlue;
const getErrorColor = (props) => (props.theme && props.theme.colors && props.theme.colors.feedback && props.theme.colors.feedback.error) || colors.error;
const getTextPrimary = (props) => (props.theme && props.theme.colors && props.theme.colors.text && props.theme.colors.text.primary) || (colors.text && colors.text.primary) || '#091b26';
const getTextSecondary = (props) => (props.theme && props.theme.colors && props.theme.colors.text && props.theme.colors.text.secondary) || '#666';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-family: ${props => getFontFamily(props)};
  position: relative;
`;

export const Label = styled.label`
  font-size: ${props => getFontSizeSm(props)};
  color: ${props => getPrimaryColor(props)};
  margin-bottom: 4px;
  font-weight: 600;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
`;

export const ErrorText = styled.span`
  color: ${props => getErrorColor(props)};
  font-size: ${props => getFontSizeXs(props)};
  margin-top: 4px;
`;

export const InputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const IconWrapper = styled.div`
  position: absolute;
  right: 0;
  padding: 0.5rem;
  color: ${props => getPrimaryColor(props)};
  pointer-events: none;
`;

export const StyledInputMask = styled(InputMask)`
  border: none;
  border-bottom: 2px solid ${props => (props.error ? colors.error : props.isFocused ? colors.brandPrimary : colors.brandLightGray)};
  width: 100%;
  min-height: auto;
  box-sizing: border-box;
  color: ${props => getTextPrimary(props)};
  font-family: ${props => getFontFamily(props)};
  outline: none;
  padding: 0.2rem 0;
  padding-right: 30px;
  background-color: transparent;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  font-size: ${props => (props.sizeVariant && typography.fontSize[props.sizeVariant] ? typography.fontSize[props.sizeVariant] : typography.fontSize.sm)};
  transition: border-color 0.2s ease-in-out;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'text')};

  &::placeholder {
    color: ${props => getTextSecondary(props)};
    font-style: italic;
  }

  &:hover {
    border-bottom: 2px solid ${props => (props.error ? colors.error : props.isFocused ? colors.brandPrimary : colors.brandMediumGray)};
  }
`;

export const CalendarPopup = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 16px;
  z-index: 9999;
  width: ${props => (props.isRange ? '600px' : '300px')};
  display: flex;
  gap: 20px;

  @media (max-width: 640px) {
    width: min(calc(100vw - 32px), 320px);
    flex-direction: column;
    max-width: 320px;
    padding: 12px;
    gap: 12px;
  }
`;

export const CalendarMonth = styled.div`
  flex: 1;
`;

export const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  color: ${colors.brandPrimary};
  font-weight: 600;

  i {
    cursor: pointer;
    padding: 4px;
    &:hover {
      color: ${props => getPrimaryColor(props)};
    }
  }
`;

export const WeekDaysRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 0.75rem;
  color: ${props => getTextSecondary(props)};
  margin-bottom: 12px;
`;

export const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  row-gap: 8px;
`;

export const DayCell = styled.div`
  font-size: ${props => getFontSizeSm(props)};
  padding: 6px 0;
  cursor: ${props => (props.isEmpty || props.disabled ? 'default' : 'pointer')};
  color: ${props => {
    if (props.isEmpty) return 'transparent';
    if (props.disabled) return colors.brandLightGray;
    if (props.isSelected || props.isRangeStart || props.isRangeEnd) return '#fff';
    return colors.dark;
  }};
  background-color: ${props => {
    if (props.isSelected || props.isRangeStart || props.isRangeEnd) return colors.brandPrimary;
    if (props.isInRange) return '#E6F1F7';
    return 'transparent';
  }};
  border-radius: ${props => {
    if (props.isRangeStart && props.isInRange) return '4px 0 0 4px';
    if (props.isRangeEnd && props.isInRange) return '0 4px 4px 0';
    if (props.isSelected || props.isRangeStart || props.isRangeEnd) return '4px';
    return '0';
  }};
  font-weight: ${props => (props.isSelected || props.isRangeStart || props.isRangeEnd ? 'bold' : 'normal')};

  &:hover {
    background-color: ${props => {
      if (props.isEmpty || props.disabled) return 'transparent';
      if (props.isSelected || props.isRangeStart || props.isRangeEnd) return colors.brandPrimary;
      return '#F5F5F5';
    }};
    border-radius: ${props => (!props.isRangeStart && !props.isRangeEnd && !props.isInRange ? '4px' : '')};
  }
`;



// WEBPACK FOOTER //
// ./src/design-system/atoms/SelectDate/components/SelectDateStyles.js